/**
 * HeroSelectionPopup.js
 */

import {
    GameWindow,
    ButtonsEvents,
    CoreEvents,
    Game,
    UiButton,
    UiImage,
    UiText,
    UiFrame,
    UiElement,
    GameTween,
    GameShape,
    GameAnimatedSprite,
    GameTimer
} from "pixi_core";

import { Common } from "../game/Common";

import {
    GameEvents,
    Heroes,
    Tutorials,
    TEAM_SIZE,
    NB_HEROES,
    DEFAULT_DRAG_THRESHOLD,
    HERO_BUTTON_SPRITES,
    HERO_BUTTON_SPRITES_OVER,
    HERO_BUTTON_SPRITES_PRESSED,
    HERO_BUTTON_SPRITES_SELECTED,
    HERO_AVAILABLE_SPRITE,
    HERO_AVAILABLE_SPRITE_OVER,
    HERO_AVAILABLE_SPRITE_SELECTED,
    HERO_AVAILABLE_SPRITE_CHOSEN,
    HERO_AVAILABLE_ABILITY,
    HERO_AVAILABLE_ABILITY_OVER,
    HERO_AVAILABLE_ABILITY_SELECTED,
    HERO_AVAILABLE_ABILITY_CHOSEN,
    HERO_FEATURED_SPRITE,
    HERO_FEATURED_ABILITY_ICON,
    HERO_FEATURED_ABILITY_PREVIEW,
    HERO_AVAILABLE_FRAME,
    HERO_AVAILABLE_BACKGROUND,
    SCALE_PRESSED,
    HERO_ABILITY_NAMES,
    HERO_NAMES,
    HERO_SELECTION_TRANSITION_DURATION,
    HERO_SELECTION_PANEL_MASK_WIDTH,
    HERO_SELECTION_PANEL_MASK_HEIGHT,
    HERO_SELECTION_DARKEN_OPACITY,
    HERO_SELECTION_SLIDING_START_X,
    HERO_SELECTION_DRAG_ANIMATION,
    HERO_SELECTION_TAP_ANIMATION,
    TOUCH_TEAM_BUTTON_Y_OFFSET
} from "../Constants";

export class HeroSelectionPopup {
    /**
     * @public @constructor
     * @param {GameWindow} parent
     */
    constructor(parent) {
        /** @private @type {GameWindow} */
        this._parent = parent;

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {Boolean} */
        this._isOpened = false;

        /** @private @type {Object} */
        this._touchMoveListener = this._onTouchMove.bind(this);

        /** @private @type {Function} */
        this._openedCallback = null;
        /** @private @type {Function} */
        this._closedCallback = null;

        /** @private @type {String[]} */
        this._heroesOrder = [
            Heroes.eBLACK_PANTHER,
            Heroes.eCAPTAIN_AMERICA,
            Heroes.eHULK,
            Heroes.eCAPTAIN_MARVEL,
            Heroes.eIRON_MAN,
            Heroes.eWASP,
            Heroes.eTHOR,
            Heroes.eBLACK_WIDOW
        ];

        /** @private @type {UiImage} */
        this._darken = null;

        /** @private @type {UiFrame} */
        this._frmActiveTeam = null;
        /** @private @type {UiFrame} */
        this._frmPanel = null;
        /** @private @type {UiFrame} - Sliding panel */
        this._frmSliding = null;

        /** @private @type {UiButton} */
        this._btnHeroSelection = null;
        /** @private @type {UiButton} */
        this._btnConfirm = null;

        /** @private @type {UiButton[]} */
        this._btnTeam = [];
        /** @private @type {UiImage[]} */
        this._imgTeam = [];

        /** @private @type {UiText} */
        this._lblTeamHP = null;

        /** @private @type {UiFrame[]} */
        this._frmHeroes = [];
        /** @private @type {UiImage[]} */
        this._imgHeroes = [];
        /** @private @type {UiImage[]} */
        this._imgHeroesBack = [];
        /** @private @type {UiImage[]} */
        this._imgAbilities = [];
        /** @private @type {UiButton[]} */
        this._btnFrames = [];

        /** @private @type {UiImage} */
        this._imgFeaturedHero = null;
        /** @private @type {UiImage} */
        this._imgFeaturedAbilityIcon = null;
        /** @private @type {UiImage} */
        this._imgFeaturedAbilityPreview = null;
        /** @private @type {UiText} */
        this._lblFeaturedAbilityName = null;
        /** @private @type {UiText} */
        this._lblFeaturedAbilityDesc = null;
        /** @private @type {UiText} */
        this._lblFeaturedHeroName = null;
        /** @private @type {UiText} */
        this._lblFeaturedHeroHP = null;
        /** @private @type {UiText} */
        this._lblFeaturedHeroTimer = null;

        /** @private @type {Number} */
        this._teamHP = 0;

        /** @private @type {Number} */
        this._selectedSlotId = -1;

        /** @private @type {Heroes} */
        this._selectedHero = null;

        /** @private @type {Heroes} */
        this._lastSelectedHero = null;

        let dragThresholdMultiplier = Game.viewport.width / Game.viewport.height;
        if (dragThresholdMultiplier < 1) dragThresholdMultiplier = 1 / dragThresholdMultiplier;

        /** @private @type {Number} */
        this._dragThreshold = Math.round(dragThresholdMultiplier * DEFAULT_DRAG_THRESHOLD);

        /** @private @type {{frame: UiFrame, hero: UiImage, name: String, fromTeam: Boolean, fromAvailable: Boolean, thresholdReached: Boolean}} */
        this._draggedHero = {
            frame: null,
            hero: null,
            name: "",
            fromTeam: false,
            fromAvailable: false,
            thresholdReached: false
        };

        /** @private @type {Heroes[]} */
        this._heroTeam = [];

        /** @private @type {Object[]} */
        this._teamButtonPos = [];

        /** @private @type {{top: Number, bottom: Number, left: Number, right: Number}[]} - Hit area of the team heroes buttons */
        this._teamButtonArea = [];

        /** @private @type {{top: Number, bottom: Number, left: Number, right: Number}[]} - Hit area of the available heroes buttons */
        this._availableButtonArea = [];

        /** @private @type {{ x: Number, y: Number }} */
        this._draggedButtonStartPos = { x: 0, y: 0 };

        /** @private @type {{ x: Number, y: Number }} */
        this._dragInputStart = { x: 0, y: 0 };

        /** @private @type {UiImage} */
        this._teamHighlight = null;

        /** @private @type {UiImage} */
        this._availableHighlight = null;

        /** @private @type {UiImage} */
        this._tutorialHandStill = null;
        /** @private @type {UiImage} */
        this._tutorialHandDrag = null;
        /** @private @type {GameAnimatedSprite} */
        this._tutorialHandAnimated = null;

        /** @private @type {GameTween} */
        this._tweenOpen = null;
        /** @private @type {GameTween} */
        this._tweenClose = null;
        /** @private @type {GameTween} */
        this._tweenDarken = null;

        /** @private @type {GameTimer} */
        this._timerHandRelease = null;
        /** @private @type {GameTween} */
        this._tweenHandDrag = null;
        /** @private @type {GameTween} */
        this._tweenHandDragBack = null;
        /** @private @type {GameTimer} */
        this._timerHandPress = null;

        /** @private @type {GameTween} */
        this._tweenFadeInRightHand = null;
        /** @private @type {GameTimer} */
        this._timerSelectAvailable = null;
        /** @private @type {GameTween} */
        this._tweenFadeOutRightHand = null;
        /** @private @type {GameTween} */
        this._tweenFadeInLeftHand = null;
        /** @private @type {GameTimer} */
        this._timerSelectActive = null;
        /** @private @type {GameTween} */
        this._tweenFadeOutLeftHand = null;

        Game.on(CoreEvents.eRAW_INPUT_DOWN, this._onRawInputDown, this);
        Game.on(CoreEvents.eRAW_INPUT_MOVE, this._onRawInputMove, this);
        Game.on(CoreEvents.eRAW_INPUT_UP, this._onRawInputUp, this);
        Game.on(CoreEvents.eGAME_PAUSED, this._onGamePaused, this);
        Game.on(CoreEvents.eLOCALE_CHANGED, this._onLocaleChanged, this);

        Game.on(GameEvents.eHERO_SELECT_TEAMUP, this._onTeamSlotTapped, this);
        Game.on(GameEvents.eHERO_SELECT_CONFIRM, this._onConfirm, this);

        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_SWITCH_AVENGERS, this._onHighlightSwitchAvengers, this);
        Game.on(GameEvents.eTUTORIAL_HIGHLIGHT_CLEAR, this._onHighlightClear, this);
    }

    /**
     * @public
     * @param {UiElement} parent
     */
    initialize(parent) {
        this._ui = Game.ui.instantiate("HeroSelectionPopup", parent);

        this._darken = this._ui.findElementByName("Darken");
        this._darken.visible = false;

        this._frmPanel = this._ui.findElementByName("Panel");
        this._frmPanel.visible = false;

        this._frmSliding = this._frmPanel.findElementByName("Sliding");

        const mask = new GameShape();
        mask.beginFill(0xffffff);
        mask.drawRect(
            -HERO_SELECTION_PANEL_MASK_WIDTH / 2,
            -HERO_SELECTION_PANEL_MASK_HEIGHT / 2,
            HERO_SELECTION_PANEL_MASK_WIDTH,
            HERO_SELECTION_PANEL_MASK_HEIGHT
        );
        mask.endFill();

        this._frmPanel.uiRoot.addChild(mask);
        this._frmPanel.uiRoot.mask = mask;

        // Active team (left side bar)
        this._frmActiveTeam = this._ui.findElementByName("ActiveTeam");
        for (let i = 0; i < TEAM_SIZE; ++i) {
            let buttonName = "BtnTeam" + i;
            let teamButton = this._frmActiveTeam.findElementByName(buttonName);
            teamButton.visible = false;

            this._teamButtonPos.push({ x: teamButton.x, y: teamButton.y });
            this._teamButtonArea.push({
                top: this._ui.y + this._frmActiveTeam.y + teamButton.y - teamButton.height / 2,
                bottom: this._ui.y + this._frmActiveTeam.y + teamButton.y + teamButton.height / 2,
                left: this._ui.x + this._frmActiveTeam.x + teamButton.x - teamButton.width / 2,
                right: this._ui.x + this._frmActiveTeam.x + teamButton.x + teamButton.width / 2
            });
            this._btnTeam.push(teamButton);

            let teamImage = this._frmActiveTeam.findElementByName("ImgTeam" + i);
            this._imgTeam.push(teamImage);
        }

        this._btnConfirm = this._frmActiveTeam.findElementByName("BtnConfirm");
        this._btnConfirm.visible = false;

        this._btnHeroSelection = this._frmActiveTeam.findElementByName("BtnHeroSelection");

        this._lblTeamHP = this._frmActiveTeam.findElementByName("HP");

        this._teamHighlight = this._frmActiveTeam.findElementByName("TeamHighlight");
        this._teamHighlight.visible = false;

        // Available heroes
        const frmAvailable = this._frmPanel.findElementByName("Available");
        for (let i = 0; i < NB_HEROES; ++i) {
            const frmHero = frmAvailable.findElementByName("Hero" + i);
            this._frmHeroes.push(frmHero);

            const lblHP = frmHero.findElementByName("HP");
            lblHP.text = Common.characters.getHP(this._heroesOrder[i]).toString();
            const lblCooldown = frmHero.findElementByName("LblCountdown");
            lblCooldown.text = Common.characters.getAbilityCooldown(this._heroesOrder[i]).toString();

            /** @type {UiButton} */
            const btnFrame = frmHero.findElementByName("Frame");
            this._btnFrames.push(btnFrame);

            btnFrame.setCallback(ButtonsEvents.eINPUT_OVER, this._onHeroOver.bind(this, i));
            btnFrame.setCallback(ButtonsEvents.eINPUT_DOWN, this._onHeroPressed.bind(this, i));
            btnFrame.setCallback(ButtonsEvents.eINPUT_UP, this._onHeroUp.bind(this, i));
            btnFrame.setCallback(ButtonsEvents.eINPUT_OUT, this._onHeroOut.bind(this, i));
            btnFrame.setCallback(ButtonsEvents.eTAP, this._onHeroTapped.bind(this, i));

            const imgHeroBack = frmHero.findElementByName("Back");
            this._imgHeroesBack.push(imgHeroBack);

            const imgHero = frmHero.findElementByName("Hero");
            this._imgHeroes.push(imgHero);

            const imgAbility = frmHero.findElementByName("Ability");
            this._imgAbilities.push(imgAbility);

            this._availableButtonArea.push({
                top: this._ui.y + frmHero.y - frmHero.height / 2,
                bottom: this._ui.y + frmHero.y + frmHero.height / 2,
                left: this._ui.x + frmHero.x - frmHero.width / 2,
                right: this._ui.x + frmHero.x + frmHero.width / 2
            });
        }

        this._availableHighlight = frmAvailable.findElementByName("Highlight");
        this._availableHighlight.visible = false;

        // Featured hero
        const frmFeatured = this._frmPanel.findElementByName("Featured");
        this._imgFeaturedHero = frmFeatured.findElementByName("Hero");
        this._lblFeaturedHeroName = frmFeatured.findElementByName("HeroName");
        this._lblFeaturedHeroHP = frmFeatured.findElementByName("HP");
        this._lblFeaturedHeroTimer = frmFeatured.findElementByName("LblHeroTimer");
        this._imgFeaturedAbilityIcon = frmFeatured.findElementByName("AbilityIcon");
        this._imgFeaturedAbilityPreview = frmFeatured.findElementByName("AbilityPreview");
        this._lblFeaturedAbilityName = frmFeatured.findElementByName("AbilityName");
        this._lblFeaturedAbilityDesc = frmFeatured.findElementByName("AbilityDesc");

        this._lblFeaturedHeroName.setLocalized(true);
        this._lblFeaturedAbilityName.setLocalized(true);
        this._lblFeaturedAbilityDesc.setLocalized(true);

        this._draggedHero.frame = this._ui.findElementByName("DraggedHero");
        this._draggedHero.frame.visible = false;
        this._draggedHero.hero = this._draggedHero.frame.findElementByName("Hero");

        this._tutorialHandStill = this._ui.findElementByName("TutorialHandStill");
        this._tutorialHandStill.visible = false;

        this._tutorialHandDrag = this._ui.findElementByName("TutorialHandDrag");
        this._tutorialHandDrag.visible = false;
    }

    /**
     * @public
     */
    loadHeroTeam() {
        this._heroTeam = Common.save.getHeroTeam();
        this._updateTeamHeroes();
    }

    /**
     * @public
     * @returns {UiButton}
     */
    get btnHeroSelection() {
        return this._btnHeroSelection;
    }

    /**
     * @public
     * @returns {UiButton}
     */
    get btnConfirm() {
        return this._btnConfirm;
    }

    /**
     * @private
     */
    _enableButtons() {
        this._btnHeroSelection.enable();
        this._btnConfirm.enable();

        for (const btnFrame of this._btnFrames) {
            btnFrame.enable();
        }

        for (const btnTeam of this._btnTeam) {
            btnTeam.enable();
        }
    }

    /**
     * @private
     */
    _disableButtons() {
        this._btnHeroSelection.disable();
        this._btnConfirm.disable();

        for (const btnFrame of this._btnFrames) {
            btnFrame.disable();
        }

        for (const btnTeam of this._btnTeam) {
            btnTeam.disable();
        }
    }

    /**
     * @public
     * @param {Function} openedCallback
     * @param {Function} closedCallback
     */
    open(openedCallback, closedCallback) {
        this._openedCallback = openedCallback;
        this._closedCallback = closedCallback;

        this._frmPanel.visible = true;

        this._btnConfirm.visible = true;
        this._btnHeroSelection.visible = false;

        for (const btnTeam of this._btnTeam) {
            btnTeam.visible = true;
        }
        for (const imgTeam of this._imgTeam) {
            imgTeam.visible = false;
        }

        this.loadHeroTeam();

        this._selectedSlotId = -1;
        this._selectedHero = null;

        this._updateAvailableHeroes();

        if (!this._selectedHero) {
            this._lastSelectedHero = this._heroTeam[0];
        }

        this._setFeaturedHero(this._heroTeam[0]);

        this._draggedHero.frame.visible = false;
        this._draggedHero.name = "";
        this._draggedHero.fromTeam = false;
        this._draggedHero.fromAvailable = false;
        this._draggedHero.thresholdReached = false;

        this._disableButtons();

        this._frmSliding.x = HERO_SELECTION_SLIDING_START_X;
        this._tweenOpen = Game.windows
            .getCurrentWindow()
            .createTween(this._frmSliding)
            .to({ x: 0 }, HERO_SELECTION_TRANSITION_DURATION)
            .easing(GameTween.Easing.CircularOut)
            .onComplete(this._onOpenTransitionCompleted.bind(this))
            .start();

        this._darken.visible = true;
        this._darken.opacity = 0;

        this._tweenDarken = Game.windows
            .getCurrentWindow()
            .createTween(this._darken)
            .to({ opacity: HERO_SELECTION_DARKEN_OPACITY }, HERO_SELECTION_TRANSITION_DURATION)
            .easing(GameTween.Easing.CircularOut)
            .start();

        document.addEventListener("touchmove", this._touchMoveListener);
        Game.audio.playSfx("IDSND_POPUP_OPEN");
    }

    /**
     * @private
     */
    _onOpenTransitionCompleted() {
        this._isOpened = true;

        if (this._tweenOpen) {
            this._tweenOpen.destroy();
            this._tweenOpen = null;
        }

        this._darken.opacity = HERO_SELECTION_DARKEN_OPACITY;
        if (this._tweenDarken) {
            this._tweenDarken.destroy();
            this._tweenDarken = null;
        }

        this._enableButtons();

        Common.tutorial.nextStep(Tutorials.eHERO_SELECTION);

        if (this._openedCallback) {
            this._openedCallback();
        }
    }

    /**
     * @private
     */
    _close() {
        this._isOpened = false;

        this._btnConfirm.visible = false;
        this._btnHeroSelection.visible = true;

        for (const btnTeam of this._btnTeam) {
            btnTeam.visible = false;
        }
        for (const imgTeam of this._imgTeam) {
            imgTeam.visible = true;
        }

        this._selectedSlotId = -1;
        this._selectedHero = null;

        this._updateTeamHeroes();
        this._updateAvailableHeroes();

        Game.fire(GameEvents.eAVENGERS_ASSEMBLE, this._heroTeam);
        Game.audio.playSfx("IDSND_POPUP_CLOSE");

        this._disableButtons();

        this._tweenClose = Game.windows
            .getCurrentWindow()
            .createTween(this._frmSliding)
            .to({ x: HERO_SELECTION_SLIDING_START_X }, HERO_SELECTION_TRANSITION_DURATION)
            .easing(GameTween.Easing.CircularIn)
            .onComplete(this._onCloseTransitionCompleted.bind(this))
            .start();

        this._tweenDarken = Game.windows
            .getCurrentWindow()
            .createTween(this._darken)
            .to({ opacity: 0 }, HERO_SELECTION_TRANSITION_DURATION)
            .easing(GameTween.Easing.CircularIn)
            .start();
    }

    /**
     * Hero selection menu is suddenly closed by a context change
     * @public
     */
    forceClose() {
        this._clearTweens();
        this._onHighlightClear();

        this._btnConfirm.visible = false;
        this._btnHeroSelection.visible = true;

        for (const btnTeam of this._btnTeam) {
            btnTeam.visible = false;
        }
        for (const imgTeam of this._imgTeam) {
            imgTeam.visible = true;
        }

        this._selectedSlotId = -1;
        this._selectedHero = null;

        this._updateTeamHeroes();
        this._updateAvailableHeroes();

        this._frmSliding.x = HERO_SELECTION_SLIDING_START_X;
        this._darken.opacity = 0;

        this._enableButtons();
        document.removeEventListener("touchmove", this._touchMoveListener);

        if (this._isOpened) {
            this._isOpened = false;
            Game.fire(GameEvents.eAVENGERS_ASSEMBLE, this._heroTeam);
        }
    }

    /**
     * @private
     */
    _onCloseTransitionCompleted() {
        this._clearTweens();

        this._darken.visible = false;
        this._frmPanel.visible = false;

        this._enableButtons();
        this._onHighlightClear();
        document.removeEventListener("touchmove", this._touchMoveListener);

        if (this._closedCallback) {
            this._closedCallback();
        }
    }

    /**
     * A touch input has moved
     * @private
     * @param {Object} event
     */
    _onTouchMove(event) {
        event.preventDefault();

        let position = Game.screenToViewport(event.targetTouches[0].clientX, event.targetTouches[0].clientY);
        for (let i = 0; i < NB_HEROES; ++i) {
            if (this._isInButton(position.x, position.y, i)) {
                this._onHeroOver(i);
            } else {
                this._onHeroOut(i);
            }
        }

        for (let i = 0; i < this._btnTeam.length; ++i) {
            this._btnTeam[i].setSelected(this._isInTeamButton(position.x, position.y, i));
        }
    }

    /**
     * Checks if the touch move is inside a selection button's area
     * @private
     * @param {Number} x
     * @param {Number} y
     * @param {Number} index
     * @returns {Boolean}
     */
    _isInButton(x, y, index) {
        return (
            x > this._frmHeroes[index].x - this._btnFrames[index].width / 2 &&
            x < this._frmHeroes[index].x + this._btnFrames[index].width / 2 &&
            y > this._frmHeroes[index].y - this._btnFrames[index].height / 2 &&
            y < this._frmHeroes[index].y + this._btnFrames[index].height / 2
        );
    }

    /**
     * Checks if the touch move is inside a team button's area
     * @private
     * @param {Number} x
     * @param {Number} y
     * @param {Number} index
     * @returns {Boolean}
     */
    _isInTeamButton(x, y, index) {
        x -= this._frmActiveTeam.x;
        y += TOUCH_TEAM_BUTTON_Y_OFFSET;

        return (
            x > this._btnTeam[index].x - this._btnTeam[index].width / 2 &&
            x < this._btnTeam[index].x + this._btnTeam[index].width / 2 &&
            y > this._btnTeam[index].y - this._btnTeam[index].height / 2 &&
            y < this._btnTeam[index].y + this._btnTeam[index].height / 2
        );
    }

    /**
     * @private
     * @param {Number} index
     */
    _onHeroOver(index) {
        const heroName = this._heroesOrder[index];

        this._imgHeroesBack[index].setSprite(HERO_AVAILABLE_BACKGROUND["over"]);
        this._imgHeroes[index].setSprite(HERO_AVAILABLE_SPRITE_OVER[heroName]);
        this._imgAbilities[index].setSprite(HERO_AVAILABLE_ABILITY_OVER[heroName]);
    }

    /**
     * @private
     * @param {Number} index
     */
    _onHeroPressed(index) {
        this._frmHeroes[index].scale = SCALE_PRESSED;

        const heroName = this._heroesOrder[index];

        if (this._selectedHero === heroName) {
            this._imgHeroesBack[index].setSprite(HERO_AVAILABLE_BACKGROUND["selected"]);
            this._imgHeroes[index].setSprite(HERO_AVAILABLE_SPRITE_SELECTED[heroName]);
            this._imgAbilities[index].setSprite(HERO_AVAILABLE_ABILITY_SELECTED[heroName]);
        } else if (this._isHeroActive(heroName)) {
            this._imgHeroesBack[index].setSprite(HERO_AVAILABLE_BACKGROUND["chosen"]);
            this._imgHeroes[index].setSprite(HERO_AVAILABLE_SPRITE_CHOSEN[heroName]);
            this._imgAbilities[index].setSprite(HERO_AVAILABLE_ABILITY_CHOSEN[heroName]);
        } else {
            this._imgHeroesBack[index].setSprite(HERO_AVAILABLE_BACKGROUND["idle"]);
            this._imgHeroes[index].setSprite(HERO_AVAILABLE_SPRITE[heroName]);
            this._imgAbilities[index].setSprite(HERO_AVAILABLE_ABILITY[heroName]);
        }
    }

    /**
     * @private
     * @param {Number} index
     */
    _onHeroUp(index) {
        this._frmHeroes[index].scale = 1;
    }

    /**
     * @private
     * @param {Number} index
     */
    _onHeroOut(index) {
        const heroName = this._heroesOrder[index];

        if (this._selectedHero === heroName) {
            this._imgHeroesBack[index].setSprite(HERO_AVAILABLE_BACKGROUND["selected"]);
            this._imgHeroes[index].setSprite(HERO_AVAILABLE_SPRITE_SELECTED[heroName]);
            this._imgAbilities[index].setSprite(HERO_AVAILABLE_ABILITY_SELECTED[heroName]);
        } else if (this._isHeroActive(heroName)) {
            this._imgHeroesBack[index].setSprite(HERO_AVAILABLE_BACKGROUND["chosen"]);
            this._imgHeroes[index].setSprite(HERO_AVAILABLE_SPRITE_CHOSEN[heroName]);
            this._imgAbilities[index].setSprite(HERO_AVAILABLE_ABILITY_CHOSEN[heroName]);
        } else {
            this._imgHeroesBack[index].setSprite(HERO_AVAILABLE_BACKGROUND["idle"]);
            this._imgHeroes[index].setSprite(HERO_AVAILABLE_SPRITE[heroName]);
            this._imgAbilities[index].setSprite(HERO_AVAILABLE_ABILITY[heroName]);
        }
    }

    /**
     * The player tapped on one of the available heroes
     * @private
     * @param {Number} index
     */
    _onHeroTapped(index) {
        const heroName = this._heroesOrder[index];

        // Same as selected hero ==> cancel selection
        if (heroName === this._selectedHero) {
            this._setSelectedHero(null);
            this._selectedSlotId = -1;
            Game.audio.playSfx("IDSND_LVL_AVEN_DESELECTED");

            if (this._parent.shouldActivateNavigation()) {
                this._btnFrames[index].setSelected(true);
            }
            return;
        }

        if (this._selectedSlotId > -1) {
            this._chooseHero(this._selectedSlotId, heroName);

            // Keep the button selected
            if (this._parent.shouldActivateNavigation()) {
                this._btnFrames[index].setSelected(true);
            }
        } else {
            this._setSelectedHero(heroName);
            this._selectedSlotId = -1;

            // GDD: If selected a hero from the Character Selection side, teleports cursor to top slot of the Active team.
            if (this._parent.shouldActivateNavigation()) {
                this._parent.setCurrentNavigationButton(this._btnTeam[0]);
                this._btnTeam[0].setSelected(true);
            }
        }
    }

    /**
     * The player tapped on one of the active team slots
     * @private
     * @param {Number} teamIndex
     */
    _onTeamSlotTapped(teamIndex) {
        const heroName = this._heroTeam[teamIndex];

        this._selectedSlotId = teamIndex;

        // Same as selected hero ==> cancel selection
        if (heroName === this._selectedHero) {
            this._setSelectedHero(null);
            this._selectedSlotId = -1;
            Game.audio.playSfx("IDSND_LVL_AVEN_DESELECTED");

            if (this._parent.shouldActivateNavigation()) {
                this._btnTeam[teamIndex].setSelected(true);
            }
            return;
        }

        if (!this._selectedHero) {
            this._setSelectedHero(heroName);

            // GDD: If selected a hero from the Active Team side, teleports cursor to the top left character of the Character Selection side.
            if (this._parent.shouldActivateNavigation()) {
                this._parent.setCurrentNavigationButton(this._btnFrames[0]);
                this._btnFrames[0].setSelected(true);
            }
        } else {
            this._chooseHero(teamIndex, this._selectedHero);

            // Keep the button selected
            if (this._parent.shouldActivateNavigation()) {
                this._btnTeam[teamIndex].setSelected(true);
            }
        }
    }

    /**
     * Applies the selected hero to the selected team slot
     * @private
     * @param {Number} teamIndex
     * @param {String} heroName
     */
    _chooseHero(teamIndex, heroName) {
        if (!heroName || heroName.length === 0) {
            console.log("HeroSelectionPopup._chooseHero with invalid hero name ==> ignore");
            return;
        }

        let alreadyAssembledButtonId = -1;
        this._teamHP = 0;

        // Is the specified hero already in the active team?
        for (let i = 0; i < TEAM_SIZE; ++i) {
            if (this._heroTeam[i] === heroName) {
                alreadyAssembledButtonId = i;
            }
        }

        if (alreadyAssembledButtonId >= 0) {
            this._heroTeam[alreadyAssembledButtonId] = this._heroTeam[teamIndex];
        }

        this._heroTeam[teamIndex] = heroName;

        this._selectedHero = null;
        this._draggedHero.name = "";
        this._selectedSlotId = -1;

        this._updateTeamHeroes();
        this._updateAvailableHeroes();
        this._setFeaturedHero(heroName);

        Game.audio.playSfx("IDSND_LVL_AVEN_SELECTED");
    }

    /**
     * @private
     * @returns {Boolean}
     */
    _isHeroActive(name) {
        return this._heroTeam.includes(name);
    }

    /**
     * Resets the selected heroes on the left side bar
     * @private
     */
    _updateTeamHeroes() {
        this._teamHP = 0;

        // Update active team buttons and total HP
        for (let i = 0; i < TEAM_SIZE; ++i) {
            const heroName = this._heroTeam[i];
            const btnTeam = this._btnTeam[i];
            const imgTeam = this._imgTeam[i];

            this._teamHP += Common.characters.getHP(heroName);

            if (this._selectedHero === heroName) {
                btnTeam.setSprite(HERO_BUTTON_SPRITES_SELECTED[heroName]);
            } else {
                btnTeam.setSprite(HERO_BUTTON_SPRITES[heroName]);
            }

            btnTeam.setSpriteOver(HERO_BUTTON_SPRITES_OVER[heroName]);
            btnTeam.setSpritePressed(HERO_BUTTON_SPRITES_PRESSED[heroName]);

            imgTeam.setSprite(HERO_BUTTON_SPRITES[heroName]);
        }
        this._lblTeamHP.setText(this._teamHP.toString());
    }

    /**
     * @private
     */
    _updateAvailableHeroes() {
        for (let i = 0; i < NB_HEROES; ++i) {
            const heroName = this._heroesOrder[i];
            if (this._selectedHero === heroName) {
                // Hero is selected
                this._btnFrames[i].setSprite(HERO_AVAILABLE_FRAME["selected"]);

                this._imgHeroesBack[i].setSprite(HERO_AVAILABLE_BACKGROUND["selected"]);
                this._imgHeroes[i].setSprite(HERO_AVAILABLE_SPRITE_SELECTED[heroName]);
                this._imgAbilities[i].setSprite(HERO_AVAILABLE_ABILITY_SELECTED[heroName]);
            } else if (this._isHeroActive(heroName)) {
                // Hero is in active team
                this._btnFrames[i].setSprite(HERO_AVAILABLE_FRAME["chosen"]);

                this._imgHeroesBack[i].setSprite(HERO_AVAILABLE_BACKGROUND["chosen"]);
                this._imgHeroes[i].setSprite(HERO_AVAILABLE_SPRITE_CHOSEN[heroName]);
                this._imgAbilities[i].setSprite(HERO_AVAILABLE_ABILITY_CHOSEN[heroName]);
            } else {
                // Hero is not selected nor in active team
                this._btnFrames[i].setSprite(HERO_AVAILABLE_FRAME["idle"]);

                this._imgHeroesBack[i].setSprite(HERO_AVAILABLE_BACKGROUND["idle"]);
                this._imgHeroes[i].setSprite(HERO_AVAILABLE_SPRITE[heroName]);
                this._imgAbilities[i].setSprite(HERO_AVAILABLE_ABILITY[heroName]);
            }
        }
    }

    /**
     * @private
     * @param {String} heroName
     */
    _setSelectedHero(heroName) {
        if (heroName) {
            Game.audio.playSfx("IDSND_LVL_AVEN_SELECTED");
        }

        this._selectedHero = heroName;

        this._setFeaturedHero(heroName);
        this._updateAvailableHeroes();
        this._updateTeamHeroes();
    }

    /**
     * @private
     * @param {String} heroName
     */
    _setFeaturedHero(heroName) {
        if (heroName) {
            this._lastSelectedHero = heroName;

            this._imgFeaturedHero.setSprite(HERO_FEATURED_SPRITE[heroName]);
            this._lblFeaturedHeroName.text = HERO_NAMES[heroName];
            this._lblFeaturedHeroHP.text = Common.characters.getHP(heroName).toString();
            this._lblFeaturedHeroTimer.text = Common.characters.getAbilityCooldown(heroName).toString();

            this._imgFeaturedAbilityIcon.setSprite(HERO_FEATURED_ABILITY_ICON[heroName]);
            this._imgFeaturedAbilityPreview.setSprite(HERO_FEATURED_ABILITY_PREVIEW[heroName]);
            this._lblFeaturedAbilityName.text = HERO_ABILITY_NAMES[heroName];
            this._lblFeaturedAbilityDesc.text = Common.heroes.getAbilityDescription(heroName);
        }
    }

    /**
     * On touch or mouse input down
     * @private
     * @param {{x: Number, y: Number}} position
     */
    _onRawInputDown(position) {
        this._draggedHero.fromAvailable = false;
        this._draggedHero.fromTeam = false;

        if (!this._isOpened) {
            return;
        }

        const uiPos = Game.screenToViewport(position.x, position.y);

        let availableIndex = this._posToAvailableHeroIndex(uiPos.x, uiPos.y);
        let teamIndex = this._posToTeamHeroIndex(uiPos.x, uiPos.y);

        if (availableIndex >= 0 || teamIndex >= 0) {
            this._draggedButtonStartPos.x = uiPos.x;
            this._draggedButtonStartPos.y = uiPos.y;
            this._dragInputStart.x = uiPos.x;
            this._dragInputStart.y = uiPos.y;

            if (availableIndex >= 0) {
                this._draggedHero.name = this._heroesOrder[availableIndex];
                this._draggedHero.fromAvailable = true;
            } else {
                this._draggedHero.name = this._heroTeam[teamIndex];
                this._draggedHero.fromTeam = true;

                this._selectedSlotId = teamIndex;
            }

            this._draggedHero.hero.setSprite(HERO_BUTTON_SPRITES[this._draggedHero.name]);
            this._draggedHero.frame.x = uiPos.x - this._ui.x;
            this._draggedHero.frame.y = uiPos.y - this._ui.y;
        }
    }

    /**
     * On touch or mouse input move
     * @private
     * @param {{x: Number, y: Number}} position
     */
    _onRawInputMove(position) {
        if (!this._isOpened) {
            return;
        }

        const uiPos = Game.screenToViewport(position.x, position.y);

        if (this._draggedHero.fromAvailable || this._draggedHero.fromTeam) {
            if (!this._draggedHero.thresholdReached) {
                if (
                    Math.abs(this._dragInputStart.x - uiPos.x) > this._dragThreshold ||
                    Math.abs(this._dragInputStart.y - uiPos.y) > this._dragThreshold
                ) {
                    this._draggedHero.thresholdReached = true;
                    this._draggedHero.frame.visible = true;

                    if (this._draggedHero.name !== this._selectedHero) {
                        this._setSelectedHero(this._draggedHero.name);
                    }
                }
            }

            if (this._draggedHero.thresholdReached) {
                this._draggedHero.frame.x = this._draggedButtonStartPos.x + (uiPos.x - this._dragInputStart.x) - this._ui.x;
                this._draggedHero.frame.y = this._draggedButtonStartPos.y + (uiPos.y - this._dragInputStart.y) - this._ui.y;
            }
        }
    }

    /**
     * On touch or mouse input up
     * @private
     * @param {{x: Number, y: Number}} position
     */
    _onRawInputUp(position) {
        if (!this._isOpened) {
            return;
        }

        const uiPos = position ? Game.screenToViewport(position.x, position.y) : null;

        if (uiPos !== null) {
            if (this._draggedHero.thresholdReached) {
                if (this._draggedHero.fromTeam) {
                    // When dragging a team button, we can target another team button or an available hero button
                    const availableIndex = this._posToAvailableHeroIndex(uiPos.x, uiPos.y);
                    const teamIndex = this._posToTeamHeroIndex(uiPos.x, uiPos.y);

                    if (availableIndex >= 0) {
                        if (this._selectedSlotId !== -1) {
                            this._chooseHero(this._selectedSlotId, this._heroesOrder[availableIndex]);
                        }
                    } else if (teamIndex >= 0) {
                        this._chooseHero(teamIndex, this._draggedHero.name);
                    }
                } else if (this._draggedHero.fromAvailable) {
                    // When dragging an available button, the only valid target is a team button
                    const teamIndex = this._posToTeamHeroIndex(uiPos.x, uiPos.y);

                    if (teamIndex >= 0) {
                        this._chooseHero(teamIndex, this._draggedHero.name);
                    } else {
                        this._selectedHero = null;
                        this._draggedHero.name = "";
                        this._selectedSlotId = -1;
                        this._setSelectedHero(null);
                    }
                }
            }
        } else {
            this._selectedHero = null;
            this._draggedHero.name = "";
            this._selectedSlotId = -1;
        }

        this._draggedHero.frame.visible = false;
        this._draggedHero.thresholdReached = false;
        this._draggedHero.fromAvailable = false;
        this._draggedHero.fromTeam = false;
    }

    /**
     * Returns the index of the available hero button under the specified ui position
     * @private
     * @param {Number} x
     * @param {Number} y
     * @returns {Number}
     */
    _posToAvailableHeroIndex(x, y) {
        for (let i = 0; i < this._availableButtonArea.length; ++i) {
            if (
                x >= this._availableButtonArea[i].left &&
                x <= this._availableButtonArea[i].right &&
                y >= this._availableButtonArea[i].top &&
                y <= this._availableButtonArea[i].bottom
            ) {
                return i;
            }
        }
        return -1;
    }

    /**
     * Returns the index of the team hero button under the specified ui position
     * @private
     * @param {Number} x
     * @param {Number} y
     * @returns {Number}
     */
    _posToTeamHeroIndex(x, y) {
        for (let i = 0; i < this._teamButtonArea.length; ++i) {
            if (
                x >= this._teamButtonArea[i].left &&
                x <= this._teamButtonArea[i].right &&
                y >= this._teamButtonArea[i].top &&
                y <= this._teamButtonArea[i].bottom
            ) {
                return i;
            }
        }

        return -1;
    }

    /**
     * The game process is paused for some reason
     * @private
     */
    _onGamePaused() {
        this._onRawInputUp(null);
    }

    /**
     * The window is closed
     * @private
     */
    _onConfirm() {
        this._close();
        Common.tutorial.nextStep(Tutorials.eHERO_SELECTION);
        Common.tutorial.saveProgress();
    }

    /**
     * @private
     */
    _onHighlightSwitchAvengers() {
        this._teamHighlight.visible = true;
        this._availableHighlight.visible = true;

        if (Common.tutorials.usingKeyboardControls) {
            this._beginTapAnimation();
        } else {
            this._beginDragAnimation();
        }
    }

    /**
     * @private
     */
    _onHighlightClear() {
        this._setSelectedHero(null);
        this._selectedSlotId = -1;

        this._teamHighlight.visible = false;
        this._availableHighlight.visible = false;

        this._tutorialHandStill.visible = false;
        this._tutorialHandDrag.visible = false;
        this._draggedHero.frame.visible = false;

        if (this._tutorialHandAnimated) {
            this._tutorialHandAnimated.stop();
            this._tutorialHandAnimated.destroy();
            this._tutorialHandAnimated = null;
        }

        if (this._timerHandRelease) {
            this._timerHandRelease.destroy();
            this._timerHandRelease = null;
        }

        if (this._tweenHandDrag) {
            this._tweenHandDrag.destroy();
            this._tweenHandDrag = null;
        }

        if (this._tweenHandDragBack) {
            this._tweenHandDragBack.destroy();
            this._tweenHandDragBack = null;
        }

        if (this._timerHandPress) {
            this._timerHandPress.destroy();
            this._timerHandPress = null;
        }

        if (this._tweenFadeInRightHand) {
            this._tweenFadeInRightHand.destroy();
            this._tweenFadeInRightHand = null;
        }

        if (this._timerSelectAvailable) {
            this._timerSelectAvailable.destroy();
            this._timerSelectAvailable = null;
        }

        if (this._tweenFadeOutRightHand) {
            this._tweenFadeOutRightHand.destroy();
            this._tweenFadeOutRightHand = null;
        }

        if (this._tweenFadeInLeftHand) {
            this._tweenFadeInLeftHand.destroy();
            this._tweenFadeInLeftHand = null;
        }

        if (this._timerSelectActive) {
            this._timerSelectActive.destroy();
            this._timerSelectActive = null;
        }

        if (this._tweenFadeOutLeftHand) {
            this._tweenFadeOutLeftHand.destroy();
            this._tweenFadeOutLeftHand = null;
        }
    }

    /**
     * @private
     */
    _beginDragAnimation() {
        this._tutorialHandStill.visible = true;
        this._tutorialHandDrag.visible = false;

        let dragPos = {
            x: HERO_SELECTION_DRAG_ANIMATION.start.x,
            y: HERO_SELECTION_DRAG_ANIMATION.start.y
        };

        this._draggedHero.hero.setSprite(HERO_BUTTON_SPRITES[this._heroesOrder[0]]);
        this._draggedHero.frame.x = dragPos.x;
        this._draggedHero.frame.y = dragPos.y;
        this._draggedHero.frame.visible = false;

        this._tutorialHandStill.x = dragPos.x;
        this._tutorialHandStill.y = dragPos.y;
        this._tutorialHandDrag.x = dragPos.x;
        this._tutorialHandDrag.y = dragPos.y;

        this._timerHandRelease = Game.windows
            .getCurrentWindow()
            .createTimer(HERO_SELECTION_DRAG_ANIMATION.delayRelease, { autoStart: false, deleteOnCompletion: false });
        this._timerHandRelease.onComplete = () => {
            this._tutorialHandStill.visible = true;
            this._tutorialHandDrag.visible = false;
            this._draggedHero.frame.visible = false;

            this._tweenHandDragBack.start();
        };

        this._tweenHandDrag = Game.windows
            .getCurrentWindow()
            .createTween(dragPos)
            .to(HERO_SELECTION_DRAG_ANIMATION.end, HERO_SELECTION_DRAG_ANIMATION.durationDrag)
            .delay(HERO_SELECTION_DRAG_ANIMATION.delayDrag)
            .onUpdate(() => {
                this._tutorialHandStill.x = dragPos.x;
                this._tutorialHandStill.y = dragPos.y;
                this._tutorialHandDrag.x = dragPos.x;
                this._tutorialHandDrag.y = dragPos.y;
                this._draggedHero.frame.x = dragPos.x;
                this._draggedHero.frame.y = dragPos.y;
            })
            .onComplete(() => {
                this._timerHandRelease.start();
            });

        let dragBackPos = {
            x: HERO_SELECTION_DRAG_ANIMATION.end.x,
            y: HERO_SELECTION_DRAG_ANIMATION.end.y
        };

        this._tweenHandDragBack = Game.windows
            .getCurrentWindow()
            .createTween(dragBackPos)
            .to(HERO_SELECTION_DRAG_ANIMATION.start, HERO_SELECTION_DRAG_ANIMATION.durationDrag)
            .delay(HERO_SELECTION_DRAG_ANIMATION.delayDrag)
            .onUpdate(() => {
                this._tutorialHandStill.x = dragBackPos.x;
                this._tutorialHandStill.y = dragBackPos.y;
                this._tutorialHandDrag.x = dragBackPos.x;
                this._tutorialHandDrag.y = dragBackPos.y;
                this._draggedHero.frame.x = dragBackPos.x;
                this._draggedHero.frame.y = dragBackPos.y;
            })
            .onComplete(() => {
                this._timerHandPress.start();
            });

        this._timerHandPress = Game.windows
            .getCurrentWindow()
            .createTimer(HERO_SELECTION_DRAG_ANIMATION.delayPress, { deleteOnCompletion: false });
        this._timerHandPress.onComplete = () => {
            this._tutorialHandStill.visible = false;
            this._tutorialHandDrag.visible = true;
            this._draggedHero.frame.visible = true;

            this._tweenHandDrag.start();
        };
    }

    /**
     * @private
     */
    _beginTapAnimation() {
        this._tutorialHandAnimated = new GameAnimatedSprite(null, Game.design.getGraphicData("TutorialHand"));
        this._tutorialHandAnimated.gotoAndStop(0);
        this._ui.uiRoot.addChild(this._tutorialHandAnimated);

        this._tutorialHandAnimated.x = HERO_SELECTION_TAP_ANIMATION.start.x;
        this._tutorialHandAnimated.y = HERO_SELECTION_TAP_ANIMATION.start.y;
        this._tutorialHandAnimated.alpha = 0;

        // 1. Fade-in hand over Black Panther
        this._tweenFadeInRightHand = Game.windows
            .getCurrentWindow()
            .createTween(this._tutorialHandAnimated)
            .to({ alpha: 1 }, HERO_SELECTION_TAP_ANIMATION.durationFadeIn)
            .delay(HERO_SELECTION_TAP_ANIMATION.delayFadeIn)
            .onStart(() => {
                this._tutorialHandAnimated.x = HERO_SELECTION_TAP_ANIMATION.start.x;
                this._tutorialHandAnimated.y = HERO_SELECTION_TAP_ANIMATION.start.y;
            })
            .onComplete(() => {
                this._tutorialHandAnimated.playAnimation("finger", false, { ignoreNonSystemPauseEvents: true });
                this._timerSelectAvailable.start();
            });

        // 2. Set BlackPanther as selected
        this._timerSelectAvailable = Game.windows
            .getCurrentWindow()
            .createTimer(HERO_SELECTION_TAP_ANIMATION.delaySelection, { autoStart: false, deleteOnCompletion: false });
        this._timerSelectAvailable.onComplete = () => {
            this._onHeroTapped(0);
            this._tweenFadeOutRightHand.start();
        };

        // 3. Fade-out hand over Black Panther + unselect
        this._tweenFadeOutRightHand = Game.windows
            .getCurrentWindow()
            .createTween(this._tutorialHandAnimated)
            .to({ alpha: 0 }, HERO_SELECTION_TAP_ANIMATION.durationFadeOut)
            .delay(HERO_SELECTION_TAP_ANIMATION.delayFadeOut)
            .onComplete(() => {
                this._setSelectedHero(null);
                this._selectedSlotId = -1;
                this._tweenFadeInLeftHand.start();
            });

        // 4. Fade-in hand over Hulk
        this._tweenFadeInLeftHand = Game.windows
            .getCurrentWindow()
            .createTween(this._tutorialHandAnimated)
            .to({ alpha: 1 }, HERO_SELECTION_TAP_ANIMATION.durationFadeIn)
            .delay(HERO_SELECTION_TAP_ANIMATION.delayFadeIn)
            .onStart(() => {
                this._tutorialHandAnimated.x = HERO_SELECTION_TAP_ANIMATION.end.x;
                this._tutorialHandAnimated.y = HERO_SELECTION_TAP_ANIMATION.end.y;
            })
            .onComplete(() => {
                this._tutorialHandAnimated.playAnimation("finger", false, { ignoreNonSystemPauseEvents: true });
                this._timerSelectActive.start();
            });

        // 5. Set BlackPanther as selected
        this._timerSelectActive = Game.windows
            .getCurrentWindow()
            .createTimer(HERO_SELECTION_TAP_ANIMATION.delaySelection, { autoStart: false, deleteOnCompletion: false });
        this._timerSelectActive.onComplete = () => {
            this._onTeamSlotTapped(0);
            this._tweenFadeOutLeftHand.start();
        };

        // 6. Fade-out hand over Hulk + unselect
        this._tweenFadeOutLeftHand = Game.windows
            .getCurrentWindow()
            .createTween(this._tutorialHandAnimated)
            .to({ alpha: 0 }, HERO_SELECTION_TAP_ANIMATION.durationFadeOut)
            .delay(HERO_SELECTION_TAP_ANIMATION.delayFadeOut)
            .onComplete(() => {
                this._setSelectedHero(null);
                this._selectedSlotId = -1;
                this._tweenFadeInRightHand.start();
            });

        this._tweenFadeInRightHand.start();
    }

    /**
     * A locale change occurred
     * @private
     * @param {String} localeId
     * @param {Boolean} rtlLocale
     */
    _onLocaleChanged(localeId, rtlLocale) {
        if (!this._isOpened) return;

        this._lblFeaturedAbilityDesc.text = Common.heroes.getAbilityDescription(this._lastSelectedHero);
    }

    /**
     * @private
     */
    _clearTweens() {
        if (this._tweenOpen) {
            this._tweenOpen.destroy();
            this._tweenOpen = null;
        }

        if (this._tweenClose) {
            this._tweenClose.destroy();
            this._tweenClose = null;
        }

        if (this._tweenDarken) {
            this._tweenDarken.destroy();
            this._tweenDarken = null;
        }

        if (this._timerHandRelease) {
            this._timerHandRelease.destroy();
            this._timerHandRelease = null;
        }

        if (this._tweenHandDrag) {
            this._tweenHandDrag.destroy();
            this._tweenHandDrag = null;
        }

        if (this._tweenHandDragBack) {
            this._tweenHandDragBack.destroy();
            this._tweenHandDragBack = null;
        }

        if (this._timerHandPress) {
            this._timerHandPress.destroy();
            this._timerHandPress = null;
        }

        if (this._tweenFadeInRightHand) {
            this._tweenFadeInRightHand.destroy();
            this._tweenFadeInRightHand = null;
        }

        if (this._timerSelectAvailable) {
            this._timerSelectAvailable.destroy();
            this._timerSelectAvailable = null;
        }

        if (this._tweenFadeOutRightHand) {
            this._tweenFadeOutRightHand.destroy();
            this._tweenFadeOutRightHand = null;
        }

        if (this._tweenFadeInLeftHand) {
            this._tweenFadeInLeftHand.destroy();
            this._tweenFadeInLeftHand = null;
        }

        if (this._timerSelectActive) {
            this._timerSelectActive.destroy();
            this._timerSelectActive = null;
        }

        if (this._tweenFadeOutLeftHand) {
            this._tweenFadeOutLeftHand.destroy();
            this._tweenFadeOutLeftHand = null;
        }
    }
}
